/**
 * DeepSeek-OCR 바운딩 박스 파서
 * 
 * DeepSeek 응답 형식:
 * <|ref|>text content<|/ref|><|det|>[[x1, y1, x2, y2]]<|/det|>
 * 
 * 좌표 형식: [x1, y1, x2, y2] (좌상단, 우하단)
 */

/**
 * DeepSeek 마크다운 텍스트에서 바운딩 박스 정보 추출
 * @param {string} markdownText - DeepSeek가 반환한 마크다운 텍스트
 * @returns {Array<{text: string, bbox: [number, number, number, number]}>}
 */
export function parseBoundingBoxes(markdownText) {
  if (!markdownText || typeof markdownText !== 'string') {
    return [];
  }

  const boundingBoxes = [];
  
  // 정규식: <|ref|>텍스트<|/ref|><|det|>[[x1, y1, x2, y2]]<|/det|>
  const regex = /<\|ref\|>(.*?)<\|\/ref\|><\|det\|>\[\[([\d\s,]+)\]\]<\|\/det\|>/g;
  
  let match;
  while ((match = regex.exec(markdownText)) !== null) {
    const text = match[1].trim();
    const coordsStr = match[2].trim();
    
    try {
      // 좌표 파싱: "x1, y1, x2, y2" → [x1, y1, x2, y2]
      const coords = coordsStr.split(',').map(s => parseFloat(s.trim()));
      
      if (coords.length === 4 && coords.every(c => !isNaN(c))) {
        boundingBoxes.push({
          text: text,
          bbox: coords, // [x1, y1, x2, y2]
          width: coords[2] - coords[0],
          height: coords[3] - coords[1]
        });
      }
    } catch (error) {
      console.warn('바운딩 박스 파싱 실패:', coordsStr, error);
    }
  }
  
  return boundingBoxes;
}

/**
 * 바운딩 박스를 이미지 크기에 맞게 스케일링
 * @param {Array} boundingBoxes - parseBoundingBoxes()의 결과
 * @param {number} originalWidth - DeepSeek가 처리한 이미지의 원본 너비
 * @param {number} originalHeight - DeepSeek가 처리한 이미지의 원본 높이
 * @param {number} displayWidth - 화면에 표시되는 이미지 너비
 * @param {number} displayHeight - 화면에 표시되는 이미지 높이
 * @returns {Array} 스케일링된 바운딩 박스
 */
export function scaleBoundingBoxes(boundingBoxes, originalWidth, originalHeight, displayWidth, displayHeight) {
  if (!boundingBoxes || boundingBoxes.length === 0) {
    return [];
  }
  
  const scaleX = displayWidth / originalWidth;
  const scaleY = displayHeight / originalHeight;
  
  return boundingBoxes.map(box => ({
    ...box,
    bbox: [
      box.bbox[0] * scaleX,
      box.bbox[1] * scaleY,
      box.bbox[2] * scaleX,
      box.bbox[3] * scaleY
    ],
    width: box.width * scaleX,
    height: box.height * scaleY
  }));
}

/**
 * 바운딩 박스 통계 정보
 * @param {Array} boundingBoxes
 * @returns {Object} { count, avgWidth, avgHeight, totalArea }
 */
export function getBoundingBoxStats(boundingBoxes) {
  if (!boundingBoxes || boundingBoxes.length === 0) {
    return { count: 0, avgWidth: 0, avgHeight: 0, totalArea: 0 };
  }
  
  const totalWidth = boundingBoxes.reduce((sum, box) => sum + box.width, 0);
  const totalHeight = boundingBoxes.reduce((sum, box) => sum + box.height, 0);
  const totalArea = boundingBoxes.reduce((sum, box) => sum + (box.width * box.height), 0);
  
  return {
    count: boundingBoxes.length,
    avgWidth: totalWidth / boundingBoxes.length,
    avgHeight: totalHeight / boundingBoxes.length,
    totalArea: totalArea
  };
}

